import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { generatePath } from 'react-router-dom';

import { Card, CardActions, CardHeader } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import { useNavigateAfterSubmit } from 'common/hooks';

import { ActionButton } from 'commonComponents/ActionButton';
import { CommonFormComponents } from 'commonComponents/CommonFormComponents';
import { LinkButton } from 'commonComponents/LinkButton';
import { PageContainer } from 'commonComponents/PageContainer';

import { useHandleFormError } from 'api/hooks/errorHandling';
import { EditIcon, UndoIcon } from 'icons';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { useCurrentUserHasRole } from 'roles/helpers';
import { ROUTES } from 'routes/routes';

import { mutateCustomer, updateCustomer } from '../api/customers';
import { CustomerFormContent, CustomerFormContentProps, CustomerFormFields } from './CustomerFormContent';

export function EditCustomer({ customer }: { customer: import('../api/customers').CustomerDetails }) {
    const navigateAfterSubmit = useNavigateAfterSubmit();
    const currentUserHasRole = useCurrentUserHasRole();
    const formContext = useForm<CustomerFormFields>({
        defaultValues: _.pick(customer, [
            'address',
            'city',
            'comment',
            'company',
            'country',
            'email',
            'external_url',
            'full_name',
            'groups',
            'is_active',
            'owner_id',
            'phone_number',
            'products',
            'username',
        ]),
    });
    const handleFormError = useHandleFormError(formContext);

    const queryClient = useQueryClient();
    const { mutate: trigger, isPending: requestInProgress } = useMutation({
        mutationFn: (values: CustomerFormFields) =>
            updateCustomer(customer.id, {
                ...customer,
                ...values,
                default_product: customer.default_product?.id,
                products: values.products?.map(p => p.id),
            }),
        onSuccess: (response, values) => {
            mutateCustomer(queryClient, response);
            formContext.reset(values);
            const customerDetailRoute = generatePath(ROUTES.CUSTOMER_DETAIL, {
                id: response.id.toString(),
            });
            if (!customer.is_active && values.is_active) {
                navigateAfterSubmit(
                    `${generatePath(ROUTES.USER_EMAIL, {
                        id: response.id.toString(),
                    })}?${new URLSearchParams({
                        returnPath: customerDetailRoute,
                    })}`
                );
            } else {
                navigateAfterSubmit(customerDetailRoute);
            }
        },
        onError: handleFormError,
        meta: { errorMessage: 'Customer update failed' },
    });

    const defaultProps: CustomerFormContentProps['defaultProps'] = {
        autoComplete: 'off',
        variant: 'outlined',
        margin: 'none',
    };

    return (
        <PageContainer maxWidth="md">
            <FormContainer formContext={formContext} onSuccess={data => trigger(data)} data-testid="EditCustomer_form">
                <CommonFormComponents />
                <Card>
                    <CardHeader title="Edit Customer" />
                    <CustomerFormContent
                        defaultProps={defaultProps}
                        form={formContext}
                        showIsActive={currentUserHasRole(ROLE_DEFINITIONS.LicenseManager)}
                    />
                    <CardActions>
                        <ActionButton
                            color="secondary"
                            iconClass={EditIcon}
                            inProgress={requestInProgress}
                            title="Submit"
                            type="submit"
                            variant="contained"
                        />
                        <LinkButton
                            iconClass={UndoIcon}
                            route={generatePath(ROUTES.CUSTOMER_DETAIL, {
                                id: customer.id.toString(),
                            })}
                            title="Cancel"
                        />
                    </CardActions>
                </Card>
            </FormContainer>
        </PageContainer>
    );
}
